export const delay = (milliseconds: number): Promise<void> => {
  return new Promise<void>((resolve) => {
    setTimeout(resolve, milliseconds);
  });
};

export type DebouncedFunction<F extends (...args: never[]) => void> = ((
  ...args: Parameters<F>
) => void) & { cancel: () => void };

export function debounce<F extends (...args: never[]) => void>(
  func: F,
  waitFor: number,
): DebouncedFunction<F> {
  let timeout: ReturnType<typeof setTimeout> | null = null;

  const setDebounce = (...args: Parameters<F>): void => {
    if (timeout !== null) {
      clearTimeout(timeout);
    }

    timeout = setTimeout(() => func(...args), waitFor);
  };

  setDebounce.cancel = () => {
    if (timeout !== null) {
      clearTimeout(timeout);
      timeout = null;
    }
  };

  return setDebounce;
}

export type ThrottledFunction<F extends (...args: never[]) => void> = ((
  ...args: Parameters<F>
) => void) & { cancel: () => void };

export function throttle<F extends (...args: never[]) => void>(
  func: F,
  waitFor: number,
): ThrottledFunction<F> {
  let timeout: ReturnType<typeof setTimeout> | null = null;
  let lastExecutionTime = 0;
  let trailingArgs: Parameters<F> | null = null;

  const execute = (...args: Parameters<F>): void => {
    lastExecutionTime = Date.now();
    func(...args);
  };

  const setThrottle = (...args: Parameters<F>): void => {
    if (waitFor <= 0) {
      execute(...args);
      return;
    }

    const now = Date.now();
    const remaining = waitFor - (now - lastExecutionTime);

    trailingArgs = args;

    if (remaining <= 0) {
      if (timeout !== null) {
        clearTimeout(timeout);
        timeout = null;
      }

      execute(...trailingArgs);
      trailingArgs = null;

      return;
    }

    if (timeout === null) {
      timeout = setTimeout(() => {
        timeout = null;

        if (trailingArgs !== null) {
          execute(...trailingArgs);
          trailingArgs = null;
        }
      }, remaining);
    }
  };

  setThrottle.cancel = () => {
    if (timeout !== null) {
      clearTimeout(timeout);
      timeout = null;
    }

    trailingArgs = null;
  };

  return setThrottle;
}
